/**
 * The multi-region scanner.
 *
 * This supersedes the single-channel `ScanSession` for anything driven by a
 * camera. It takes RGB frames from several facial regions and applies, in order:
 *
 *   1. POS chrominance extraction per region — rejects motion and illumination
 *      change, which a green-only estimator cannot distinguish from a pulse.
 *   2. Spectral fusion across regions, weighted by each region's own quality.
 *   3. Four independent lock criteria, all of which must agree.
 *
 * The four criteria are deliberately different in kind, so that a single mode of
 * failure cannot satisfy all of them at once:
 *
 *   prominence  — the peak stands above the rest of the band
 *   harmonic    — there is energy at 2f, as a cardiac pulse has and noise has not
 *   stability   — the estimate has stopped moving between evaluations
 *   agreement   — independent regions of the same face report the same rate
 *
 * Agreement is the one that is genuinely new here, and it is the closest thing
 * to a second opinion the system has. Three regions of one face cannot
 * coincidentally agree on a wrong heart rate for the same reason two witnesses
 * rarely invent the same lie.
 */

import { bandPeak, bandRange, harmonicSupport, powerSpectrum } from './dsp.js';
import { pos, greenOnly, chrominancePerfusion } from './chrominance.js';

const EXTRACTOR_FNS = { pos, green: greenOnly };
import { crossMethodAgreement, fuseRegions, regionAgreement } from './fusion.js';
import { ScanState, variabilityIndexMs, ENGINE_VERSION } from './estimator.js';

export const SCANNER_VERSION = '2.0.0';

export const SCANNER_DEFAULTS = Object.freeze({
  fs: 30,
  pulseBand: [0.7, 4.0],
  respiratoryBand: [0.1, 0.5],
  padFactor: 4,
  excludeHz: 0.25,
  minSamples: 256,
  maxSamples: 1500, // 50 s — fusion earns its keep on longer windows
  stepSamples: 30,
  prominenceThreshold: 3.0,
  harmonicThreshold: 1.5,
  stabilityBpm: 1.5,
  /** Fraction of contributing regions that must agree on the rate. */
  agreementThreshold: 0.9,
  /** Below this many usable regions, agreement cannot be assessed and is not required. */
  minRegionsForAgreement: 2,
  /** Extraction families must agree within this many bpm before a lock is allowed. */
  methodToleranceBpm: 5,
  /** Frames whose quality gate failed are counted but not used. */
  maxRejectedFraction: 0.5,
  /**
   * Which extractors to run on every region.
   *
   * Both, deliberately. POS rejects motion but halves the pulse and pulls in
   * the blue channel, whose relative noise grows twentyfold across the tone
   * scale — so it weakens exactly where we need it most. Green-only keeps the
   * full pulse at the dark end but cannot tell a heartbeat from a head
   * movement. Neither dominates. Running both and weighting by measured
   * prominence lets the conditions choose, and disagreement between the two
   * families is caught by the agreement gate rather than averaged into a
   * plausible wrong answer.
   */
  extractors: ['pos', 'green'],
});

export function resolveScannerConfig(opts = {}) {
  const cfg = { ...SCANNER_DEFAULTS };
  for (const [k, v] of Object.entries(opts)) if (v !== undefined) cfg[k] = v;
  return cfg;
}

export class RegionScanSession {
  /**
   * @param {object} opts
   * @param {number} [opts.regions] how many regions frames will carry
   */
  constructor(opts = {}) {
    this.cfg = resolveScannerConfig(opts);
    this.regionCount = opts.regions ?? 3;
    this.length = 0;
    this.rejected = 0;
    this.state = ScanState.IDLE;
    this.previousBpm = null;
    this.lastDeltaBpm = null;
    this.conventional = null;
    this.latest = null;
    this.result = null;
    this.startedAt = null;

    const n = this.cfg.maxSamples;
    this.buffers = Array.from({ length: this.regionCount }, () => ({
      r: new Float64Array(n),
      g: new Float64Array(n),
      b: new Float64Array(n),
    }));
  }

  get seconds() {
    return this.length / this.cfg.fs;
  }

  get progress() {
    return Math.min(1, this.length / this.cfg.maxSamples);
  }

  /** Per-region channel views over the samples collected so far. */
  views() {
    return this.buffers.map((buf) => ({
      r: buf.r.subarray(0, this.length),
      g: buf.g.subarray(0, this.length),
      b: buf.b.subarray(0, this.length),
    }));
  }

  /**
   * Push one frame.
   * @param {Array<{r:number,g:number,b:number}>} frame one entry per region
   * @param {{accepted?: boolean}} [quality] result of the caller's frame gate
   */
  pushFrame(frame, quality = {}) {
    if (this.state === ScanState.LOCKED || this.state === ScanState.NO_SIGNAL) return this.state;
    if (this.state === ScanState.IDLE) {
      this.state = ScanState.ACQUIRING;
      this.startedAt = Date.now();
    }

    // A frame the caller judged unusable — too much motion, blown exposure,
    // face lost — is counted so we can report honestly on it, but never
    // appended. Interpolating across it would fabricate signal.
    if (quality.accepted === false) {
      this.rejected += 1;
      return this.state;
    }
    if (!Array.isArray(frame) || frame.length === 0) return this.state;

    if (this.length < this.cfg.maxSamples) {
      for (let k = 0; k < this.buffers.length; k++) {
        const px = frame[Math.min(k, frame.length - 1)];
        if (!px || !Number.isFinite(px.g)) continue;
        this.buffers[k].r[this.length] = px.r;
        this.buffers[k].g[this.length] = px.g;
        this.buffers[k].b[this.length] = px.b;
      }
      this.length += 1;
    }

    const { minSamples, stepSamples, maxSamples } = this.cfg;
    if (this.length >= minSamples && (this.length - minSamples) % stepSamples === 0) {
      this.evaluate();
    }
    if (this.state === ScanState.ACQUIRING && this.length >= maxSamples) {
      this.finish(false);
    }
    return this.state;
  }

  /** Current analysis without changing state — used to drive the live view. */
  analyseNow() {
    if (this.length < 64) return null;
    const cfg = this.cfg;
    const regions = this.views();

    // One signal per (region x extractor). Labelled so the diagnostics view can
    // show which family produced which estimate.
    const pulses = [];
    const labels = [];
    for (const name of cfg.extractors) {
      const fn = EXTRACTOR_FNS[name];
      if (!fn) continue;
      regions.forEach((region, i) => {
        pulses.push(fn(region));
        labels.push({ method: name, region: i });
      });
    }
    // The primary trace, used for respiration, variability and the waveform
    // view: POS from the best-perfused region when available, green otherwise.
    const primary = pulses[0];

    const fusion = fuseRegions(pulses, {
      fs: cfg.fs,
      padFactor: cfg.padFactor,
      band: cfg.pulseBand,
      excludeHz: cfg.excludeHz,
    });
    if (!fusion || !fusion.spectrum) return null;

    const peak = bandPeak(fusion.spectrum, cfg.pulseBand[0], cfg.pulseBand[1], {
      excludeHz: cfg.excludeHz,
    });
    if (!peak) return null;

    const labelled = fusion.perRegion.map((r, i) => ({ ...r, ...labels[i] }));
    const agreement = regionAgreement(labelled);
    const methods = crossMethodAgreement(labelled, cfg.methodToleranceBpm);

    return {
      bpm: peak.freq * 60,
      freq: peak.freq,
      prominence: peak.prominence,
      snr: peak.snr,
      harmonic: harmonicSupport(fusion.spectrum, peak.freq, peak.bandMedian),
      agreement: agreement.agreement,
      spreadBpm: agreement.spreadBpm,
      contributingRegions: agreement.contributing,
      methodsAgree: methods.agreed,
      methodSpreadBpm: methods.spreadBpm,
      byMethod: methods.byMethod,
      perRegion: labelled,
      spectrum: fusion.spectrum,
      pulses,
      primary,
    };
  }

  evaluate() {
    const reading = this.analyseNow();
    if (!reading) return;
    this.latest = reading;

    // What a conventional app would report from the same data: green channel,
    // one region, first fixed window, no further checks. Snapshotted once.
    if (this.conventional === null && this.length >= this.cfg.minSamples) {
      const first = this.views()[0];
      const window = {
        r: first.r.subarray(0, this.cfg.minSamples),
        g: first.g.subarray(0, this.cfg.minSamples),
        b: first.b.subarray(0, this.cfg.minSamples),
      };
      const spec = powerSpectrum(greenOnly(window), { fs: this.cfg.fs, padFactor: this.cfg.padFactor });
      const pk = bandPeak(spec, this.cfg.pulseBand[0], this.cfg.pulseBand[1], { excludeHz: this.cfg.excludeHz });
      if (pk) this.conventional = { bpm: pk.freq * 60, prominence: pk.prominence };
    }

    const cfg = this.cfg;
    const prominent = reading.prominence >= cfg.prominenceThreshold;
    const harmonic = reading.harmonic >= cfg.harmonicThreshold;
    const settled =
      this.previousBpm !== null && Math.abs(reading.bpm - this.previousBpm) <= cfg.stabilityBpm;
    const agreed =
      reading.contributingRegions < cfg.minRegionsForAgreement ||
      reading.agreement >= cfg.agreementThreshold;

    // Recorded so a clinician can see which of the five gates actually failed.
    // Without it, stability is the one criterion that is evaluated and then
    // thrown away, and "why did it refuse" has a hole in it.
    this.lastDeltaBpm =
      this.previousBpm === null ? null : Math.abs(reading.bpm - this.previousBpm);

    if (prominent && harmonic && settled && agreed && reading.methodsAgree) {
      this.finish(true);
      return;
    }
    this.previousBpm = reading.bpm;
  }

  finish(locked) {
    const reading = this.latest || this.analyseNow();
    this.state = locked ? ScanState.LOCKED : ScanState.NO_SIGNAL;

    const cfg = this.cfg;
    const regions = this.views();
    let breathsPerMinute = null;
    if (locked && reading) {
      const spec = powerSpectrum(reading.primary, { fs: cfg.fs, padFactor: cfg.padFactor });
      const rp = bandPeak(spec, cfg.respiratoryBand[0], cfg.respiratoryBand[1], { excludeHz: 0.05 });
      if (rp) breathsPerMinute = Number((rp.freq * 60).toFixed(1));
    }

    const totalFrames = this.length + this.rejected;
    this.result = {
      locked,
      engineVersion: ENGINE_VERSION,
      scannerVersion: SCANNER_VERSION,
      method: 'pos+fusion',
      seconds: Number(this.seconds.toFixed(2)),
      samples: this.length,
      rejectedFrames: this.rejected,
      frameAcceptance: totalFrames ? Number((this.length / totalFrames).toFixed(3)) : 1,
      regions: this.regionCount,
      heartRateBpm: locked && reading ? Number(reading.bpm.toFixed(1)) : null,
      breathsPerMinute,
      variabilityMs:
        locked && reading ? round1(variabilityIndexMs(reading.primary, reading.bpm, cfg)) : null,
      prominence: reading ? Number(reading.prominence.toFixed(2)) : null,
      harmonic: reading ? Number(reading.harmonic.toFixed(2)) : null,
      agreement: reading ? Number(reading.agreement.toFixed(2)) : null,
      spreadBpm: reading ? Number(reading.spreadBpm.toFixed(1)) : null,
      stabilityBpm: this.lastDeltaBpm === null ? null : Number(this.lastDeltaBpm.toFixed(2)),
      methodsAgree: reading ? reading.methodsAgree : null,
      methodSpreadBpm: reading ? Number(reading.methodSpreadBpm.toFixed(1)) : null,
      byMethod: reading ? reading.byMethod : {},
      contributingRegions: reading ? reading.contributingRegions : 0,
      perRegion: reading
        ? reading.perRegion.map((r) => ({
            method: r.method,
            region: r.region,
            bpm: r.bpm === null ? null : Number(r.bpm.toFixed(1)),
            prominence: Number(r.prominence.toFixed(2)),
          }))
        : [],
      perfusionIndex: reading
        ? Number(chrominancePerfusion(regions[0], reading.primary).toFixed(6))
        : 0,
      conventionalBpm: this.conventional ? Number(this.conventional.bpm.toFixed(1)) : null,
    };
    return this.result;
  }

  /**
   * The pulse band of the fused spectrum, as something a person can look at.
   *
   * This exists because "prominence 2.1, threshold 3.0" is a sentence only an
   * engineer can act on. The same fact drawn — a peak, the tallest rival it
   * has to beat, and the line three times above that rival — is legible to
   * anyone, and it is the honest picture of a refusal: nothing was tall
   * enough. Returns null until there is something real to draw, so a caller
   * can render nothing rather than an empty axis pretending to be a
   * measurement.
   */
  spectrumView() {
    const reading = this.latest;
    if (!reading || !reading.spectrum) return null;

    const spec = reading.spectrum;
    const cfg = this.cfg;
    const { first, last } = bandRange(spec, cfg.pulseBand[0], cfg.pulseBand[1]);
    if (last <= first) return null;

    const bins = [];
    for (let k = first; k <= last; k++) {
      bins.push({ bpm: k * spec.df * 60, power: spec.power[k] });
    }

    // The competitor is recomputed here rather than carried on the reading,
    // because it is the bar the peak actually has to clear and the drawing is
    // a lie without it. Same exclusion zone the lock decision uses.
    const peakIndex = Math.round(reading.freq / spec.df);
    const exclude = Math.max(1, Math.round(cfg.excludeHz / spec.df));
    let competitor = 0;
    for (let k = first; k <= last; k++) {
      if (Math.abs(k - peakIndex) > exclude && spec.power[k] > competitor) {
        competitor = spec.power[k];
      }
    }

    return {
      bins,
      peakBpm: reading.bpm,
      peakPower: spec.power[Math.max(first, Math.min(last, peakIndex))],
      competitorPower: competitor,
      thresholdPower: competitor * cfg.prominenceThreshold,
      clears: reading.prominence >= cfg.prominenceThreshold,
      loBpm: cfg.pulseBand[0] * 60,
      hiBpm: cfg.pulseBand[1] * 60,
    };
  }

  reset() {
    this.length = 0;
    this.rejected = 0;
    this.state = ScanState.IDLE;
    this.previousBpm = null;
    this.lastDeltaBpm = null;
    this.conventional = null;
    this.latest = null;
    this.result = null;
    this.startedAt = null;
  }
}

function round1(v) {
  return v === null || v === undefined ? null : Number(v.toFixed(1));
}

/** Drive a complete scan over pre-generated regions. Used by tests and sweeps. */
export function runRegionScan(regions, opts = {}) {
  const n = regions[0].g.length;
  const session = new RegionScanSession({ ...opts, regions: regions.length });
  for (let i = 0; i < n; i++) {
    const frame = regions.map((reg) => ({ r: reg.r[i], g: reg.g[i], b: reg.b[i] }));
    if (session.pushFrame(frame) !== ScanState.ACQUIRING) break;
  }
  if (!session.result) session.finish(false);
  return session.result;
}
